import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orgUnitMemberService } from '@/services/core/OrgUnitMemberService.custom';
import { OrgUnitMember, CreateOrgUnitMember, UpdateOrgUnitMember } from '@/types/core/OrgUnitMember';

export const orgUnitMemberKeys = {
  all: ['orgunit-members'] as const,
  byOrgUnit: (orgUnitId: string) => [...orgUnitMemberKeys.all, 'by-orgunit', orgUnitId] as const,
  byUser: (userId: string) => [...orgUnitMemberKeys.all, 'by-user', userId] as const,
  myMemberships: () => [...orgUnitMemberKeys.all, 'my'] as const,
};

/** Members of a specific OrgUnit (active only) */
export function useOrgUnitMembers(orgUnitId: string) {
  return useQuery<OrgUnitMember[], Error>({
    queryKey: orgUnitMemberKeys.byOrgUnit(orgUnitId),
    queryFn: () => orgUnitMemberService.getByOrgUnit(orgUnitId),
    enabled: !!orgUnitId,
  });
}

/** Current user's own memberships */
export function useMyMemberships() {
  return useQuery<OrgUnitMember[], Error>({
    queryKey: orgUnitMemberKeys.myMemberships(),
    queryFn: () => orgUnitMemberService.getMyMemberships(),
  });
}

/** Memberships for a specific user */
export function useUserMemberships(userId: string) {
  return useQuery<OrgUnitMember[], Error>({
    queryKey: orgUnitMemberKeys.byUser(userId),
    queryFn: () => orgUnitMemberService.getByUser(userId),
    enabled: !!userId,
  });
}

/** Create a new membership */
export function useCreateOrgUnitMember() {
  const queryClient = useQueryClient();
  return useMutation<OrgUnitMember, Error, CreateOrgUnitMember>({
    mutationFn: (data) => orgUnitMemberService.create(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: orgUnitMemberKeys.byOrgUnit(variables.orgUnitId) });
      queryClient.invalidateQueries({ queryKey: orgUnitMemberKeys.all });
    },
  });
}

/** Update an existing membership */
export function useUpdateOrgUnitMember() {
  const queryClient = useQueryClient();
  return useMutation<OrgUnitMember, Error, { id: string; data: UpdateOrgUnitMember }>({
    mutationFn: ({ id, data }) => orgUnitMemberService.update(id, data),
    onSuccess: (_, { data }) => {
      queryClient.invalidateQueries({ queryKey: orgUnitMemberKeys.byOrgUnit(data.orgUnitId) });
      queryClient.invalidateQueries({ queryKey: orgUnitMemberKeys.all });
    },
  });
}

/** Delete a membership */
export function useDeleteOrgUnitMember() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { id: string; orgUnitId: string }>({
    mutationFn: ({ id }) => orgUnitMemberService.delete(id),
    onSuccess: (_, { orgUnitId }) => {
      queryClient.invalidateQueries({ queryKey: orgUnitMemberKeys.byOrgUnit(orgUnitId) });
      queryClient.invalidateQueries({ queryKey: orgUnitMemberKeys.all });
    },
  });
}
